import React, { useRef, useState, useEffect } from 'react';
import { Column, CardType } from './KanbanBoard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

interface WorkBoardCanvasProps {
  columns: Column[];
  cards: { [cardId: string]: CardType };
  boardId?: string;
  userId?: string;
  onAddCard?: (colId: string) => void;
  onDeleteCard?: (colId: string, cardId: string) => void;
  onEditCard?: (colId: string, cardId: string) => void;
  onPositionsChange?: (positions: { [cardId: string]: { x: number; y: number } }) => void;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.15;

// Default position generator for new columns
const getDefaultColPos = (colIdx: number) => ({
  x: 80 + colIdx * 340,
  y: 100,
});

const COL_WIDTH = 320;
const COL_HEIGHT = 500;
const COL_LABEL_HEIGHT = 40;

const WorkBoardCanvas: React.FC<WorkBoardCanvasProps> = ({ columns, cards, boardId, userId, onAddCard, onDeleteCard, onEditCard, onPositionsChange }) => {
  const [zoom, setZoom] = useState(1);
  // Store column positions in local state (by colId)
  const [colPositions, setColPositions] = useState<{ [colId: string]: { x: number; y: number } }>({});
  const [dragging, setDragging] = useState<{ colId: string; offsetX: number; offsetY: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Initialize positions for columns that don't have one
  useEffect(() => {
    setColPositions(prev => {
      const next = { ...prev };
      columns.forEach((col, colIdx) => {
        if (!next[col.id]) {
          next[col.id] = getDefaultColPos(colIdx);
        }
      });
      return next;
    });
    // eslint-disable-next-line
  }, [columns.map(c => c.id).join(",")]);

  // Center the board function
  const centerBoard = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollWidth = container.scrollWidth;
      const scrollHeight = container.scrollHeight;
      const clientWidth = container.clientWidth;
      const clientHeight = container.clientHeight;
      
      // Center horizontally and vertically
      const centerX = Math.max(0, (scrollWidth - clientWidth) / 2);
      const centerY = Math.max(0, (scrollHeight - clientHeight) / 2);
      
      container.scrollTo({
        left: centerX,
        top: centerY,
        behavior: 'smooth'
      });
    }
  };

  // Handle wheel zoom
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setZoom(prev => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, prev + delta)));
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, []);

  // Handle mouse events for panning
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 1 || (e.button === 0 && e.altKey)) { // Middle click or Alt+Left click
        e.preventDefault();
        setIsPanning(true);
        setLastPanPoint({ x: e.clientX, y: e.clientY });
        container.style.cursor = 'grabbing';
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isPanning) {
        e.preventDefault();
        const deltaX = e.clientX - lastPanPoint.x;
        const deltaY = e.clientY - lastPanPoint.y;
        
        container.scrollBy({
          left: -deltaX,
          top: -deltaY,
          behavior: 'auto'
        });
        
        setLastPanPoint({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      setIsPanning(false);
      container.style.cursor = 'grab';
    };

    const handleMouseLeave = () => {
      setIsPanning(false);
      container.style.cursor = 'grab';
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isPanning, lastPanPoint]);

  // Drag handlers for columns
  const handleColMouseDown = (e: React.MouseEvent, colId: string) => {
    const colDiv = e.currentTarget as HTMLDivElement;
    const rect = colDiv.getBoundingClientRect();
    setDragging({
      colId,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setColPositions(pos => ({
      ...pos,
      [dragging.colId]: {
        x: (e.clientX - dragging.offsetX - 60) / zoom,
        y: (e.clientY - dragging.offsetY - 60) / zoom,
      },
    }));
  };
  const handleMouseUp = () => setDragging(null);

  const handleZoomIn = () => setZoom(prev => Math.min(ZOOM_MAX, prev + ZOOM_STEP));
  const handleZoomOut = () => setZoom(prev => Math.max(ZOOM_MIN, prev - ZOOM_STEP));
  const handleZoomReset = () => {
    setZoom(1);
    setTimeout(() => centerBoard(), 100);
  };

  // Canvas size
  const canvasWidth = Math.max(1200, columns.length * 340);
  const canvasHeight = 1200;

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl overflow-hidden">
      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg border border-slate-200">
        <button 
          onClick={handleZoomOut}
          className="w-8 h-8 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 transition-colors text-slate-600 hover:text-slate-800"
          disabled={zoom <= ZOOM_MIN}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        
        <div className="flex items-center gap-1 px-2">
          <span className="text-sm font-mono text-slate-600 min-w-[3rem] text-center">
            {(zoom * 100).toFixed(0)}%
          </span>
        </div>
        
        <button 
          onClick={handleZoomIn}
          className="w-8 h-8 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 transition-colors text-slate-600 hover:text-slate-800"
          disabled={zoom >= ZOOM_MAX}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        
        <div className="w-px h-6 bg-slate-300 mx-1"></div>
        
        <button 
          onClick={handleZoomReset}
          className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
        >
          Reset
        </button>
      </div>

      {/* Pan Instructions */}
      <div className="absolute bottom-4 left-4 z-20 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg border border-slate-200 text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>
          <span>Alt+Click or Middle Click to pan</span>
        </div>
      </div>

      {/* Scrollable Canvas Container */}
      <div 
        ref={scrollContainerRef}
        className="w-full h-full overflow-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 cursor-grab active:cursor-grabbing"
        style={{ 
          height: 'calc(100vh - 240px)',
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 #f1f5f9',
          maxWidth: '100%',
          maxHeight: '100%'
        }}
      >
        <div 
          className="p-6 min-w-max"
          style={{ 
            transform: `scale(${zoom})`, 
            transformOrigin: 'top left', 
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            minHeight: '100%',
            minWidth: 'max-content',
            maxWidth: 'none',
            width: canvasWidth,
            height: canvasHeight,
            position: 'relative'
          }}
        >
          {columns.map((col, idx) => {
            const pos = colPositions[col.id] || getDefaultColPos(idx);
            return (
              <div
                key={col.id}
                className="absolute bg-slate-100 rounded-xl shadow-lg border border-slate-200"
                style={{
                  left: pos.x,
                  top: pos.y,
                  width: COL_WIDTH,
                  minHeight: COL_LABEL_HEIGHT + 80,
                  background: '#f1f5f9',
                  borderRadius: 12,
                  boxShadow: '0 2px 8px #0001',
                  zIndex: dragging?.colId === col.id ? 10 : 2,
                  cursor: dragging?.colId === col.id ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  transition: dragging?.colId === col.id ? 'none' : 'box-shadow 0.2s',
                }}
                onMouseDown={e => handleColMouseDown(e, col.id)}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
              >
                <div
                  style={{
                    width: '100%',
                    height: COL_LABEL_HEIGHT,
                    background: '#e2e8f0',
                    borderRadius: '12px 12px 0 0',
                    textAlign: 'center',
                    lineHeight: COL_LABEL_HEIGHT + 'px',
                    fontWeight: 700,
                    fontSize: 18,
                    boxShadow: '0 2px 8px #0001',
                  }}
                >
                  {col.title}
                  {onAddCard && (
                    <button className="btn btn-xs ml-2" style={{ float: 'right', marginTop: 6 }} onClick={e => { e.stopPropagation(); onAddCard(col.id); }}>+ Add Card</button>
                  )}
                </div>
                <div style={{ padding: 8 }}>
                  {col.cardIds.map(cardId => {
                    const card = cards[cardId];
                    if (!card) return null;
                    return (
                      <div
                        key={cardId}
                        className="bg-white rounded-lg shadow p-3 mb-2 flex flex-col gap-1 relative border border-gray-200 cursor-pointer hover:bg-slate-50 transition"
                        style={{ borderLeft: `6px solid ${card.color || '#64748b'}` }}
                        onDoubleClick={() => onEditCard?.(col.id, cardId)}
                      >
                        <button
                          className="absolute top-2 right-2 text-xs text-gray-400 hover:text-red-600"
                          style={{ zIndex: 20 }}
                          onClick={ev => {
                            ev.stopPropagation();
                            onDeleteCard?.(col.id, cardId);
                          }}
                          title="Delete card"
                        >
                          ×
                        </button>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-bold text-slate-800 truncate">{card.title}</span>
                          <span className="ml-auto px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-500">
                            {card.type === 'vuln' ? '🐞 Vuln' : card.type === 'tool' ? '🔧 Tool' : card.type === 'payload' ? '💥 Payload' : card.type === 'resource' ? '🔗 Link' : '📝 Note'}
                          </span>
                        </div>
                        {/* Card content preview or badges */}
                        {card.type === 'vuln' && card.content?.severity && (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold text-white ${card.content.severity === 'Critical' ? 'bg-red-600' : card.content.severity === 'High' ? 'bg-orange-500' : card.content.severity === 'Medium' ? 'bg-yellow-400 text-slate-900' : 'bg-green-500'}`}>{card.content.severity}</span>
                        )}
                        {/* Tags */}
                        {card.tags && card.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {card.tags.map(tag => (
                              <span key={tag} className="bg-slate-200 rounded px-2 py-0.5 text-xs">{tag}</span>
                            ))}
                          </div>
                        )}
                        {/* Markdown preview for note, vuln.description, vuln.poc */}
                        {card.type === 'note' && (
                          <div className="mt-1 text-xs text-slate-700 line-clamp-4 break-words">
                            <div className="text-xs whitespace-pre-wrap">
                              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{typeof card.content === 'string' ? card.content : card.content?.note || ''}</ReactMarkdown>
                            </div>
                          </div>
                        )}
                        {card.type === 'vuln' && (
                          <>
                            {card.content?.description && (
                              <div className="mt-1 text-xs text-slate-700 line-clamp-3 break-words">
                                <div className="text-xs whitespace-pre-wrap">
                                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{card.content.description}</ReactMarkdown>
                                </div>
                              </div>
                            )}
                            {card.content?.poc && (
                              <div className="mt-1 text-xs text-slate-700 line-clamp-2 break-words">
                                <div className="text-xs whitespace-pre-wrap">
                                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{card.content.poc}</ReactMarkdown>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        {/* Custom fields */}
                        {Array.isArray(card.content?.customFields) && card.content.customFields.length > 0 && (
                          <div className="mt-1 flex flex-col gap-1">
                            {card.content.customFields.map((f: any, i: number) => (
                              <div key={i} className="flex gap-2 text-xs">
                                <span className="font-semibold text-slate-500">{f.key}:</span>
                                <span className="text-slate-700">{f.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkBoardCanvas; 